import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { Clipboard } from '@angular/cdk/clipboard';

import { CcStudyParticipant } from '../../../../../models/cc-study-participant';
import { Intervention } from '../../../../../models/intervention';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';
import { AppState } from '../../../../../state';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import {
  selectIsLoadingSelectedStudy,
  selectSelectedStudy,
  selectSelectedStudyLoaded,
} from '../../../../state/study/study.selectors';
import {
  selectCcStudyParticipants,
  selectCcStudyParticipantsLoaded,
  selectIsLoadingCcStudyParticipants,
} from '../../../../state/study-participant/cc-study-participant/cc-study-participant.selectors';
import { selectInterventions } from '../../../../state/intervention/intervention.selectors';
import { openDashboardConfirmationDialog } from '../../../../state/dashboard.actions';
import { deleteCcStudyParticipant } from '../../../../state/study-participant/cc-study-participant/cc-study-participant.actions';
import { Study } from '../../../../../models/study';
import { environment } from '../../../../../../environments/environment';

const DEFAULT_COLUMNS_TO_DISPLAY = {
  PARTICIPANTS: 'participants',
  REMOVE: 'remove',
};

@Component({
  selector: 'dashboard-cc-study-participants',
  templateUrl: './cc-study-participants.component.html',
  styleUrls: ['./cc-study-participants.component.scss'],
})
export class CcStudyParticipantsComponent implements OnInit, OnDestroy {
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  DEFAULT_COLUMNS_TO_DISPLAY = DEFAULT_COLUMNS_TO_DISPLAY;

  columnsToDisplay = [];

  loadingSelectedStudy$: Observable<boolean>;
  selectedStudyLoaded$: Observable<boolean>;

  loadingCcStudyParticipants$: Observable<boolean>;
  ccStudyParticipantsLoaded$: Observable<boolean>;

  ccStudyParticipants: CcStudyParticipant[];

  interventions: Intervention[];

  selectedStudy: Study;

  ccFeedbackUrl = environment.ccFeedbackUrl;

  columnsInitialized = false;

  subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    public privilegeService: PrivilegeService,
    private clipboard: Clipboard,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadingSelectedStudy$ = this.store.select(selectIsLoadingSelectedStudy);
    this.selectedStudyLoaded$ = this.store.select(selectSelectedStudyLoaded);

    this.loadingCcStudyParticipants$ = this.store.select(selectIsLoadingCcStudyParticipants);
    this.ccStudyParticipantsLoaded$ = this.store.select(selectCcStudyParticipantsLoaded);

    this.subscriptions.add(
      this.store
        .select(selectCcStudyParticipants)
        .subscribe((ccStudyParticipants) => (this.ccStudyParticipants = ccStudyParticipants))
    );

    this.subscriptions.add(
      this.store.select(selectInterventions).subscribe((interventions) => {
        this.interventions = interventions;
        if (!this.columnsInitialized && this.interventions?.length) {
          this.populateColumnsToDisplay();
          this.columnsInitialized = true;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => (this.selectedStudy = selectedStudy))
    );
  }

  populateColumnsToDisplay(): void {
    if (this.interventions.length) {
      this.columnsToDisplay.push(this.DEFAULT_COLUMNS_TO_DISPLAY.PARTICIPANTS);
      this.interventions.forEach((intervention) => this.columnsToDisplay.push(intervention.name));
      this.columnsToDisplay.push(this.DEFAULT_COLUMNS_TO_DISPLAY.REMOVE);
    }
  }

  getParticipantInterventionRelation(
    ccStudyParticipant: CcStudyParticipant,
    intervention: Intervention
  ): CcStudyParticipant {
    let participantIntervention = {};
    this.ccStudyParticipants.forEach((currentCcStudyParticipant) => {
      if (currentCcStudyParticipant.id === ccStudyParticipant.id) {
        const interventionToFind = currentCcStudyParticipant.interventions.find(
          (currentIntervention) => currentIntervention.interventionID === intervention.id
        );
        participantIntervention = interventionToFind
          ? {
              id: ccStudyParticipant.id,
              ...interventionToFind,
            }
          : undefined;
      }
    });
    return participantIntervention;
  }

  getCcStudyParticipantTableName(ccStudyParticipant: CcStudyParticipant): string {
    return ccStudyParticipant.email.includes('@thecareercompass.nl') ? 'Anonymous User' : ccStudyParticipant.email;
  }

  deleteCcStudyParticipant(studyId: number, ccStudyParticipantId: number): void {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: 'Are you sure you want to delete this participant?',
          onConfirm: deleteCcStudyParticipant({
            studyId,
            ccStudyParticipantId,
          }),
        },
      })
    );
  }

  copyFeedbackLinkToClipboard(ccStudyParticipant: CcStudyParticipant): void {
    if (!ccStudyParticipant?.userHash) {
      return;
    }

    const feedbackLink = `${this.ccFeedbackUrl}/${ccStudyParticipant?.interventionHash}/user/${ccStudyParticipant?.userHash}`;
    this.clipboard.copy(feedbackLink);
    this.toastrService.info(feedbackLink, 'Feedback link copied to clipboard');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
